import { Component, OnInit, OnDestroy, Inject } from '@angular/core';

import { Subscription, switchMap, filter, tap, of, catchError, take, combineLatest } from 'rxjs';

import { Location } from '@shared/location/models';
import { AuthUser } from '@shared/signup/models';
import { ConfirmationModalRef, ConfirmationModalService, } from '@tablesready/common-ui';
import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import {
  AccountApiService,
  AuthLocationService,
  AuthRestaurantService,
} from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import {
  PartiesService,
  PricePlansRestrictionsService,
  PricePlansRestrictionsModalService,
} from '../../../services';

@Component({
  selector: 'tablesready-organization',
  templateUrl: './organization.component.html',
  styleUrls: ['./organization.component.scss'],
})
export class OrganizationComponent implements OnInit, OnDestroy {

  user: AuthUser;
  locations: Location[];
  icon = AppIcon;
  iconSize = AppIconSize;
  hasBusinessPlanLocation = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly locationService: AuthLocationService,
    // TODO: replace with service who provides with locations bound with account
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly alertsService: AlertsService,
    private readonly accountApiService: AccountApiService,
    private readonly partiesService: PartiesService,
    private readonly confirmationModalService: ConfirmationModalService,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly pricePlansRestrictionsModalService: PricePlansRestrictionsModalService,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) { }

  deleteAccount(): void {
    this.confirmationModalService.confirm({
      title: 'Delete account',
      content: `<span>You are about to delete your account. Please contact
                <span class="text-link">support@tablesready.com</span>
                in order to restore the account within ${this.appVariables.ACCOUNT_DELETING_DELAY}.</span>
                <br>
                <span>Do you want to continue?</span>`,
      closeBtnText: 'Decline',
      backdropDismiss: false,
    })
      .pipe(
        take(1),
        filter((result: ConfirmationModalRef<boolean>) => result?.isConfirmed),
        switchMap(() => this.partiesService.getUpcomingForAccount()),
        switchMap((count) => {
          if (count === 0) {
            return of({ isConfirmed: true });
          }

          return this.confirmationModalService.confirm({
            content: 'Are you sure you want to delete the account? There are upcoming parties on it’s list',
            closeBtnText: 'Decline',
            backdropDismiss: false,
          });
        }),
        take(1),
        filter((result: ConfirmationModalRef<boolean>) => result?.isConfirmed),
        switchMap(() => this.accountApiService.deleteAccount()),
        tap(() => this.alertsService.addInfo(`You deleted your account! Please contact support@tablesready.com in order to restore the account within ${this.appVariables.ACCOUNT_DELETING_DELAY}.`)),
        catchError(() => {
          this.alertsService.addError('an error occurred during removing account');

          return of(null);
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.authRestaurantService.selectWithAccount()
        .pipe(
          switchMap(user => combineLatest([
            of(user),
            this.locationService.selectAll().pipe(tap(locations => {
              this.locations = locations.filter(location => location.accountId === user.account.id);
            })),
          ])),
          tap(([user, locations]) => {
            this.user = user;

            for (const loc of locations) {
              const availableForBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(user.account.subscriptionType, loc.recurly);

              if (availableForBusinessPlan) {
                this.hasBusinessPlanLocation = true;
                break;
              }
            }
          }),
        ).subscribe(),
    );
  }

  freePlanDetected(): void {
    this.pricePlansRestrictionsModalService.display(this.user).subscribe();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
